import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Newspaper, Image as ImageIcon, MessageSquare, 
  Briefcase, Settings, LogOut, FileText, Users as UsersIcon, KeyRound, X, Globe, Languages,
  Sparkles, PhoneCall, Megaphone, ShieldCheck
} from 'lucide-react';
import { getBackendUrl, getImageUrl } from '../../lib/utils';

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  // Logo state
  const [logoUrl, setLogoUrl] = useState(null);

  // Password Modal states
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState('');
  const [passLoading, setPassLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const fetchLogo = async () => {
        try {
          const url = getBackendUrl();
          const { data } = await axios.get(`${url}/api/settings`);
          if (data && data.footer_settings && data.footer_settings.logo_url) {
            setLogoUrl(data.footer_settings.logo_url);
          }
          if (data && data.image_converter_settings && data.image_converter_settings.format) {
            localStorage.setItem('target_image_format', data.image_converter_settings.format);
          } else {
            localStorage.setItem('target_image_format', 'webp');
          }
        } catch (e) {
          console.error("Error loading logo in layout", e);
        }
      };
      fetchLogo();
    }
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600 font-medium">Yükleniyor...</div>;
  if (!user || (user.role !== 'admin' && user.role !== 'editor')) return <Navigate to="/admin/login" />;

  const menu = [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, label: 'Dashboard', roles: ['admin', 'editor'] },
    { path: '/admin/news', icon: <Newspaper size={20} />, label: 'Haberler', roles: ['admin', 'editor'] },
    { path: '/admin/gallery', icon: <ImageIcon size={20} />, label: 'Galeri', roles: ['admin', 'editor'] },
    { path: '/admin/image-converter', icon: <Sparkles size={20} />, label: 'Görsel Dönüştürücü', roles: ['admin', 'editor'] },
    { path: '/admin/quick-contact', icon: <PhoneCall size={20} />, label: 'WhatsApp & Tel', roles: ['admin'] },
    { path: '/admin/popup', icon: <Megaphone size={20} />, label: 'Duyuru Popup', roles: ['admin'] },
    { path: '/admin/cookies', icon: <ShieldCheck size={20} />, label: 'Çerez Politikası (KVKK)', roles: ['admin'] },
    { path: '/admin/contact', icon: <MessageSquare size={20} />, label: 'İletişim Mesajları', roles: ['admin'] },
    { path: '/admin/hr', icon: <Briefcase size={20} />, label: 'İK Başvuruları', roles: ['admin'] },
    { path: '/admin/pages', icon: <FileText size={20} />, label: 'Sayfalar & Sözleşmeler', roles: ['admin'] },
    { path: '/admin/users', icon: <UsersIcon size={20} />, label: 'Kullanıcılar', roles: ['admin'] },
    { path: '/admin/seo', icon: <Globe size={20} />, label: 'SEO Ayarları', roles: ['admin'] },
    { path: '/admin/languages', icon: <Languages size={20} />, label: 'Dil Ayarları', roles: ['admin'] },
    { path: '/admin/settings', icon: <Settings size={20} />, label: 'Site Ayarları (CMS)', roles: ['admin'] },
  ];

  // Route Access Control (Redirect editor away from admin-only pages)
  const currentItem = menu.find(item => location.pathname === item.path || (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path)));
  if (currentItem && !currentItem.roles.includes(user.role)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const visibleMenu = menu.filter(item => item.roles.includes(user.role));

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setPassError("Yeni şifreler eşleşmiyor.");
      return;
    }
    if (newPassword.length < 6) {
      setPassError("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }
    setPassLoading(true);
    setPassError('');
    setPassSuccess('');
    try {
      const url = getBackendUrl();
      await axios.post(`${url}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      setPassSuccess("Şifreniz başarıyla güncellendi!");
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => {
        setPasswordModalOpen(false);
        setPassSuccess('');
      }, 2000);
    } catch (err) {
      setPassError(err.response?.data?.detail || "Şifre değiştirilemedi.");
    } finally {
      setPassLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 font-sans antialiased">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200/80 flex flex-col shadow-sm">
        <div className="p-6 border-b border-slate-100 flex items-center justify-center min-h-[73px]">
          {logoUrl ? (
            <img src={getImageUrl(logoUrl)} alt="Logo" className="max-h-10 w-auto object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-extrabold text-lg shadow-sm">İ</div>
              <h2 className="text-xl font-bold tracking-tight text-slate-800">İSİLKOOP <span className="text-blue-600 text-xs font-semibold px-2 py-0.5 rounded bg-blue-50">CMS</span></h2>
            </div>
          )}
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 mb-2">Yönetim</div>
          {visibleMenu.map(item => {
            const isActive = location.pathname === item.path || (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm font-medium ${
                  isActive 
                    ? 'bg-blue-50 text-blue-600 shadow-sm shadow-blue-50' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <span className={isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}>
                  {item.icon}
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-slate-100 space-y-2 bg-white">
          <div className="bg-slate-50 p-3 rounded-xl flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-sm">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="overflow-hidden flex-1">
              <h4 className="text-xs font-bold text-slate-800 truncate">{user.name}</h4>
              <p className="text-[10px] text-slate-500 truncate">{user.email}</p>
            </div>
          </div>
          
          <button 
            onClick={() => setPasswordModalOpen(true)}
            className="flex items-center gap-3 px-4 py-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 w-full text-xs font-bold"
          >
            <KeyRound size={16} className="text-slate-400" />
            Şifre Değiştir
          </button>
          
          <button 
            onClick={logout}
            className="flex items-center gap-3 px-4 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50/50 rounded-xl transition-all duration-200 w-full text-xs font-bold"
          >
            <LogOut size={16} className="text-slate-400" />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200/80 px-8 py-4 flex justify-between items-center z-10 shadow-sm shadow-slate-100">
          <h1 className="text-lg font-bold text-slate-800 capitalize">
            {location.pathname.split('/').pop().replace('-', ' ') || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              target="_blank" 
              rel="noreferrer" 
              className="text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
            >
              Siteyi Görüntüle
            </a>
            <div className="w-px h-6 bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full uppercase tracking-wider">
                {user.role}
              </span>
            </div>
          </div>
        </header>
        
        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Password Change Modal */}
      {passwordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-md border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-extrabold text-slate-800 text-sm flex items-center gap-2">
                <KeyRound size={16} className="text-blue-600" />
                Şifre Değiştir
              </h3>
              <button 
                onClick={() => {
                  setPasswordModalOpen(false);
                  setPassError('');
                  setPassSuccess('');
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                }}
                className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-xl transition"
              >
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={handlePasswordChangeSubmit} className="p-6 space-y-4">
              {passError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs font-bold text-red-600">
                  {passError}
                </div>
              )}
              {passSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs font-bold text-green-600">
                  {passSuccess}
                </div>
              )}
              
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Mevcut Şifre</label>
                <input 
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Mevcut şifrenizi girin"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Yeni Şifre</label>
                <input 
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="En az 6 karakter girin"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Yeni Şifre Tekrar</label>
                <input 
                  type="password"
                  required
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Yeni şifreyi onaylayın"
                />
              </div>

              <button
                type="submit"
                disabled={passLoading}
                className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-xs font-bold shadow-lg shadow-blue-600/10 transition disabled:opacity-50"
              >
                {passLoading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
